import { useTheme, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { CardProps } from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { formatNumber, fPercent, } from 'src/utils/format-number';
import { formatCurrency } from 'src/utils/format-currency';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings/selectors';
import { ColorSchema } from 'src/theme/palette';
import { fDate } from 'src/utils/format-time';
import { useState } from 'react';
import Skeleton from '@mui/material/Skeleton';
import Iconify from 'src/components/iconify';
import CompareChip from 'src/components/chip/compare-chip';
import { useSelector } from 'react-redux';
import 'src/theme/glass-tooltip.css';

interface SeriesData {
  x: string;
  y: number;
}

interface Props extends CardProps {
  title: string;
  total: number;
  compare: number;
  color?: ColorSchema | string;
  isPercent?: boolean;
  isCurrency?: boolean;
  series?: SeriesData[];
  definition?: string;
  isFetching?: boolean;
  kpiName?: string;
  invertedColor?: boolean;
}

export default function BankingWidgetSummary({ ...props }: Props) {
  const theme = useTheme();
  const filterStore = useSelector((state: any) => state.filters);
  const { sx, title, total, color, series, isPercent, isCurrency, definition, isFetching, compare, invertedColor, ...otherProps } = props;
  const workspace: any = useCurrentWorkspace();
  const locale = localStorage.getItem('i18nextLng') || 'en';
  const currency = workspace?.currency || 'USD';

  // Fonction pour obtenir la couleur correcte
  const getColorValue = (colorProp?: ColorSchema | string) => {
    if (!colorProp) return theme.palette.primary.main;

    // Si c'est une couleur hexadécimale, l'utiliser directement
    if (typeof colorProp === 'string' && colorProp.startsWith('#')) {
      return colorProp;
    }

    // Si c'est une couleur du thème, l'utiliser
    if (typeof colorProp === 'string' && colorProp in theme.palette) {
      return theme.palette[colorProp as ColorSchema].main;
    }

    // Fallback vers primary
    return theme.palette.primary.main;
  };

  const colorValue = getColorValue(color);

  const formatValue = (value: number, isPercent: boolean) => {
    if (isPercent) {
      return `${fPercent(value)}`;
    }
    if (isCurrency) {
      return `${formatCurrency(value, currency, locale, true, 2)}`;
    }
    return formatNumber(value);
  };

  const [isLineHover, setIsLineHover] = useState(false);

  const compareValue = (total: number, compare: number) => {
    if (compare) {
      const diff = total - compare;
      const diffPercent = diff / compare * 100;
      return diffPercent;
    }
    return undefined;
  };

  const [markerVisibleState, setMarkerVisibleState] = useState(false);

  const chartOptions: any = {
    chart: {
      sparkline: { enabled: true },
      animations: {
        enabled: true,
        easing: 'linear',
        speed: 800,
        animateGradually: { enabled: true, delay: 150 },
      },
    },
    markers: {
      size: 0,
      shape: 'circle',
      colors: ['#ffffff'],
      strokeWidth: 2,
      strokeColors: markerVisibleState ? [theme.palette.mode === 'dark' ? '#B6B6B6' : colorValue] : alpha('#ffffff', 0),
      fillOpacity: [theme.palette.mode === 'dark' ? 0 : 1],
      showNullDataPoints: true
    },
    stroke: {
      width: 2,
      curve: 'smooth',
      colors: ['rgba(126, 135, 148, 1)'],
    },
    tooltip: {
      shared: true,
      intersect: false,
      cssClass: "glass-tooltip",
      custom: function ({ series, dataPointIndex }: any) {
        const dataPoint = props.series?.[dataPointIndex];
        const tooltipFontColor = theme.palette.mode === 'dark' ? '#ffffff' : '#637381';
        return `
        <div style="
          display: inline-block;
          padding: 4px 6px;
          color: ${tooltipFontColor};
          border-radius: 4px;
        ">
          <div style="display: flex; flex-direction: column; gap: 4px;">
            <div style="
              color: ${tooltipFontColor};
              font-size: 11px;
              line-height: 12px;
              font-family: 'Public Sans', Helvetica;
              font-weight: 400;
            ">
              ${fDate(dataPoint?.x, 'yyyy-MM-dd') || ''}
            </div>
            <div style="
              color: ${tooltipFontColor};
              font-size: 11px;
              line-height: 12px;
              font-family: 'Public Sans', Helvetica;
              font-weight: 600;
            ">
              ${formatValue(series[0][dataPointIndex], props.isPercent ?? false)}
            </div>
          </div>
        </div>
      `;
      },
      y: {
        formatter: (val: number) => `${val?.toFixed(0)}`,
      },
    },
    xaxis: {
      type: 'category',
      labels: { show: false },
      crosshairs: { show: false },
    },
    yaxis: {
      labels: { show: false },
      crosshairs: { show: false },
    },
    grid: { show: false },
    fill: {
      type: 'solid',
      colors: ['rgba(126, 135, 148, 1)'],
    },
    colors: ['rgba(126, 135, 148, 1)'],
  };


  return (
    <Stack
      sx={{
        borderRadius: 2,
        bgcolor: 'background.paper',
        borderColor: "#F2F2F2",
        width: 1,
        height: '100px',
        border: '1px solid rgba(145, 158, 171, 0.20)',
        position: 'relative',
        overflow: 'hidden',
        ...sx,
      }}
      {...otherProps}
    >
      {isFetching ? (
        <Skeleton
          width={'100%'}
          height={'100px'}
          style={{ borderRadius: 10 }}
          variant="rounded"
          animation="wave"
        >
          <>
            <Iconify py="auto" icon="eva:checkmark-circle-2-fill" color="success.main" />
            <Typography color="success.main">Active</Typography>
          </>
        </Skeleton>) : (
        <Box sx={{
          width: 1,
          height: 1,
          position: 'relative',
          p: 3
        }}>
          {/* Élément décoratif */}
          <Box
            sx={{
              width: 235,
              height: 235,
              position: 'absolute',
              top: -180,                // 🔒 reste en haut
              left: '65%',              // ➜ centre horizontalement
              transform: 'translateX(-50%)',
              bgcolor: colorValue,
              opacity: 0.20,
              filter: 'blur(80px)',
              borderRadius: '50%',
              pointerEvents: 'none',
            }}
          />

          <Stack sx={{ position: 'relative' }}>
            <Box position="absolute" top={0} left={0} maxWidth="55%">
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography
                  variant="body2"
                  sx={{
                    color: '#637381',
                    fontSize: '15px',
                    fontWeight: '400',
                    lineHeight: '20px',
                  }}
                >
                  {title}
                </Typography>
                {definition && (
                  <Tooltip
                    title={definition}
                    placement="top"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: theme.palette.mode === 'dark' ? '#1A1A1A' : '#ffffff',
                          color: theme.palette.mode === 'dark' ? '#ffffff' : '#637381',
                          fontSize: '12px',
                          fontFamily: 'Public Sans, Helvetica',
                          fontWeight: '600',
                          lineHeight: '18px',
                          borderRadius: '4px',
                          padding: '8px 12px',
                          boxShadow: theme.customShadows.z16,
                        }
                      }
                    }}
                  >
                    <Box component="span" sx={{ display: 'inline-flex', cursor: 'help' }}>
                      <Iconify
                        icon="eva:info-outline"
                        width={14}
                        height={14}
                        sx={{ color: 'text.disabled' }}
                      />
                    </Box>
                  </Tooltip>
                )}
              </Stack>
            </Box>

            <Box position="absolute" top={0} right={0}>
              <Stack >
                <Typography
                  variant="h4"
                  sx={{
                    color: 'rgba(51, 60, 71, 1)',
                    fontSize: '24px',
                    fontWeight: '700',
                    lineHeight: '20px',
                  }}>
                  {formatValue(total, isPercent ?? false)}
                </Typography>
              </Stack>
            </Box>
          </Stack>

          {filterStore.compare && (
            <Box display={"flex"} justifyContent={"flex-end"} mt={4} position={'relative'} left={8}>
              <CompareChip
                value={compareValue(total, compare) ?? 0}
                invertedColor={invertedColor}
              />
            </Box>
          )}
        </Box>
      )}
    </Stack>
  );
}
